import { useContext, useEffect, useState } from 'react'
import { UIContext } from 'Context/UI'
import { useNavigate } from 'react-router-dom'
import { useProfileStore } from 'Store/Profile'
import { useCallbackPrompt } from 'Hooks/useCallbackPrompt'
import { shallow } from 'zustand/shallow'
import { toast } from 'react-toastify'
import { generateImageUrl } from 'Utils/generateImageUrl'
import Swal from 'sweetalert2'

import { TABS } from '../Config'

const VIEW_INDEX = 1

const ViewModel = () => {
	const navigate = useNavigate()
	const { setShowContentToolbar, setCustomContentToolbar } = useContext(UIContext)
	const { profile, getProfile, updateProfile, addGalleryImage, removeGalleryImage } =
		useProfileStore(
			state => ({
				profile: state.profile,
				getProfile: state.getProfile,
				updateProfile: state.updateProfile,
				addGalleryImage: state.addGalleryImage,
				removeGalleryImage: state.removeGalleryImage,
			}),
			shallow
		)
	const [formState, setFormState] = useState({
		id: 0,
		code: '',
		slug: '',
		displayTitle: '',
		contactPersonFullname: '',
		contactPersonPosition: '',
		supplierType: '',
		businessDescription: '',
		email: '',
		website: '',
		companyName: '',
		companyAddress: '',
		companyTaxNo: '',
		minimumProductionQuantity: '',
		totalEmployee: 0,
		companyPictureCover: null,
		companyLogo: null,
		companyCard: null,
		companyFactoryPicture: null,
		companyVatRegistrationFile: null,
		companyCertificateFile: null,
		companyIsoFile: null,
		companyOtherFile: null,
		galleryImages: [],
	})
	const [hasChanged, setHasChanged] = useState(false)
	const [isSubmitting, setIsSubmitting] = useState(false)

	const [openLightBoxGallery, setOpenLightBoxGallery] = useState(false)
	const [galleryImageIndex, setGalleryImageIndex] = useState(0)
	const [openGalleryImageForm, setOpenGalleryImageForm] = useState(false)
	const [galleryImage, setGalleryImage] = useState<File | null>(null)
	const [isSubmittingGalleryImageForm, setIsSubmittingGalleryImageForm] = useState(false)

	useCallbackPrompt(hasChanged)

	const findTabPath = (tabIndex: number) => {
		const tab = TABS.find((_, idx) => idx === tabIndex)
		if (tab) {
			return tab.path
		}
	}

	const onOpenLightBoxGallery = (index: number) => {
		setGalleryImageIndex(index)
		setOpenLightBoxGallery(true)
	}

	const onCloseLightBoxGallery = () => {
		setOpenLightBoxGallery(false)
	}

	const onOpenGalleryImageForm = () => {
		setOpenGalleryImageForm(true)
	}

	const onCloseGalleryImageForm = () => {
		setOpenGalleryImageForm(false)
		setGalleryImage(null)
	}

	const onChangeGalleryImage = (image: File | null) => {
		setGalleryImage(image)
	}

	const onSubmitGalleryImageForm = () => {
		setIsSubmittingGalleryImageForm(true)
		addGalleryImage(galleryImage).then(async ({ success, data }) => {
			if (!success) {
				toast.error(data)
				setIsSubmittingGalleryImageForm(false)
				return
			} else {
				const { data: pData } = await getProfile()
				setFormState({
					...pData,
					companyCard: pData.companyCard ? generateImageUrl(pData.companyCard) : null,
					companyPictureCover: pData.companyPictureCover
						? generateImageUrl(pData.companyPictureCover)
						: null,
					companyFactoryPicture: pData.companyFactoryPicture
						? generateImageUrl(pData.companyFactoryPicture)
						: null,
					galleryImages: pData.galleryImages.map((image: any) => ({
						...image,
						path: generateImageUrl(image.path),
					})),
				})

				toast.success('เพิ่มรูปภาพสำเร็จ')
				setOpenGalleryImageForm(false)
				setGalleryImage(null)
				setIsSubmittingGalleryImageForm(false)
			}
		})
	}

	const onRemoveGalleryImage = (imageId: string) => {
		Swal.fire({
			title: 'ยืนยันการลบรูปภาพ',
			text: 'คุณต้องการลบรูปภาพนี้ใช่หรือไม่',
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: 'ใช่',
			cancelButtonText: 'ไม่ใช่',
		}).then(async result => {
			if (result.isConfirmed) {
				const { success, data } = await removeGalleryImage(imageId)
				if (!success) {
					toast.error(data)
					return
				} else {
					const { data: pData } = await getProfile()
					setFormState({
						...pData,
						companyCard: pData.companyCard ? generateImageUrl(pData.companyCard) : null,
						companyPictureCover: pData.companyPictureCover
							? generateImageUrl(pData.companyPictureCover)
							: null,
						companyFactoryPicture: pData.companyFactoryPicture
							? generateImageUrl(pData.companyFactoryPicture)
							: null,
						galleryImages: pData.galleryImages.map((image: any) => ({
							...image,
							path: generateImageUrl(image.path),
						})),
					})

					toast.success('ลบรูปภาพสำเร็จ')
				}
			}
		})
	}

	const onChangeTab = (tabIndex: number) => {
		if (tabIndex !== VIEW_INDEX) {
			navigate(findTabPath(tabIndex) ?? '#')
		}
	}

	const onChange = (name: string, value: any) => {
		if (['companyCard', 'companyPictureCover', 'companyFactoryPicture'].includes(name)) {
			if (value === null) {
				setFormState({
					...formState,
					[name]: value,
				})
				setHasChanged(true)
				return
			} else if (!value.type.includes('image')) {
				toast.error('กรุณาอัพโหลดไฟล์รูปภาพเท่านั้น')
				return
			} else {
				setFormState({
					...formState,
					[name]: value,
				})
				setHasChanged(true)
				return
			}
		} else {
			setFormState({
				...formState,
				[name]: value,
			})

			setHasChanged(true)
		}
	}

	const onSubmit = async () => {
		if (hasChanged) {
			setIsSubmitting(true)
			const body: { [key: string]: any } = {}

			const allFields = ['companyCard', 'companyPictureCover', 'companyFactoryPicture']
			allFields.forEach(field => {
				// If file is exist
				if (profile[field]) {
					// If file is File
					// @ts-ignore
					if (formState[field] && typeof formState[field] !== 'string') {
						// @ts-ignore
						body[field] = formState[field]
					}
					// If file is deleted
					// @ts-ignore
					else if (!formState[field]) {
						// @ts-ignore
						body[field] = null
					}
				}
				// If file is empty
				else {
					// @ts-ignore
					if (formState[field]) {
						// @ts-ignore
						body[field] = formState[field]
					}
				}
			})

			if (Object.keys(body).length > 0) {
				const { success, data } = await updateProfile(body)
				if (!success) {
					toast.error(data)
					setIsSubmitting(false)
					return
				} else {
					const { data: pData } = await getProfile()
					setFormState({
						...pData,
						companyCard: pData.companyCard ? generateImageUrl(pData.companyCard) : null,
						companyPictureCover: pData.companyPictureCover
							? generateImageUrl(pData.companyPictureCover)
							: null,
						companyFactoryPicture: pData.companyFactoryPicture
							? generateImageUrl(pData.companyFactoryPicture)
							: null,
					})
					toast.success('บันทึกข้อมูลสำเร็จ')
					setHasChanged(false)
					setIsSubmitting(false)
				}
			} else {
				setIsSubmitting(false)
			}
		}
	}
	useEffect(() => {
		setShowContentToolbar(false)
		setCustomContentToolbar(null)

		return () => {
			setShowContentToolbar(true)
			setCustomContentToolbar(null)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		if (profile) {
			setFormState({
				...profile,
				companyCard: profile.companyCard ? generateImageUrl(profile.companyCard) : null,
				companyPictureCover: profile.companyPictureCover
					? generateImageUrl(profile.companyPictureCover)
					: null,
				companyFactoryPicture: profile.companyFactoryPicture
					? generateImageUrl(profile.companyFactoryPicture)
					: null,
				galleryImages: profile.galleryImages.map((image: any) => ({
					...image,
					path: generateImageUrl(image.path),
				})),
			})
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	return {
		companyName: profile?.companyName,
		currentTab: VIEW_INDEX,
		formState,
		onChange,
		onChangeTab,
		onSubmit,
		isSubmitting,
		isSubmittingGalleryImageForm,
		galleryImage,
		openGalleryImageForm,
		onOpenGalleryImageForm,
		onCloseGalleryImageForm,
		onChangeGalleryImage,
		onSubmitGalleryImageForm,
		onRemoveGalleryImage,
		openLightBoxGallery,
		onOpenLightBoxGallery,
		onCloseLightBoxGallery,
		galleryImageIndex,
	}
}

export default ViewModel
